import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import Loader from '../../_components/molecules/Loader';
import { Divider, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import PatientDetailsSideContent, {
  HealthProfileAccordionData,
  PatientProfileAccordionData,
} from '../../_components/organisms/PatientDetailsSideContent';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { useStores } from '../../stores';
import {
  getHealthProfileAccordionData,
  getPatientProfileAccordionData,
} from '../../core-utils/formatters/PatientDetailsPageFormatters';
import Snackbar from '../../_components/atoms/Snackbar';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import {
  BasicTable,
  CardComponent,
  Columns,
  Nurse,
  PageTitleAndSubTitle,
  Pharmacist,
  StarRating,
  Button,
  TextField,
  Edit,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import componentConstants from '../../core-utils/messages/componentConstants';
import {
  SimpleSearch,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import PatientCasesTabs from '../../_components/molecules/PatientCasesTabs';
import {
  getRatingColor,
  getRatingText,
} from '../../core-utils/helper-utils/helper';
import {
  CARE_NAVIGATOR_PROFILE,
  PATIENT_CASES,
  PLAN_DOCUMENTS_INTERNAL,
} from '../../core-utils/routes';
import { PatientCasesMessages } from '../../core-utils/messages/PatientCasesMessages';
import { formatEngagementReportData } from '../../core-utils/helper-utils/patientCasesHelper';
import EndCaseCareNavModel from '../../_components/organisms/EndCaseCareNavModal';
import TabSelection from '../../_components/molecules/TabSelection';
import PatientCaseHistoryTab from '../../_components/molecules/PatientCaseHistoryTab';
import AddInteractionModal from '../../_components/organisms/AddInteractionModal';
import moment from 'moment';
import { Interaction } from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/InteractionCard';

interface PatientCasesPageProps {}

// constants
const hospitalVisitData = [
  ['Inpatient', 0],
  ['Outpatient', 0],
  ['Emergency', 0],
  ['Office Visit ', 0],
  ['Urgent Care', 0],
];
const enrollmentData = [
  ['Plan', ''],
  ['Effective Date', ''],
  ['Termination Date', ''],
];

const PatientCasesPage: React.FC<PatientCasesPageProps> = observer(({}) => {
  const { classes } = makeStyles()({
    sideContentGridItem: {
      height: 'calc(100vh - 59px)',
      width: '346px',
      backgroundColor: patientAccessTheme.palette.status.white,
      boxSizing: 'border-box',
      border: ` solid ${patientAccessTheme.palette.grey[300]}`,
      borderWidth: '1px 1px 1px 0px',
      overflowY: 'auto',
      ...scrollBarStyles,
    },
    mainContentGridItem: {
      height: 'calc(100vh - 59px)',
      width: 'calc(100% - 346px)',
      overflowY: 'auto',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      '&::-webkit-scrollbar': {
        width: '5px',
        borderRadius: '12px',
        backgroundColor: patientAccessTheme.palette.primary.light,
      },
      '&::-webkit-scrollbar-track': {
        background: patientAccessTheme.palette.grey[300],
        borderRadius: '12px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: patientAccessTheme.palette.grey[500],
        borderRadius: '12px',
      },
      flexWrap: 'nowrap',
    },
    closedCaseContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      margin: '31px 34px 30px 34px',
    },
    editIcon: {
      height: '22px',
      width: '22px',
      '& path': {
        fill: patientAccessTheme.palette.primary.main,
      },
    },
    tabsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      '& .MuiGrid-item': {
        margin: '0px !important',
        '& .MuiTabs-root': {
          marginLeft: '34px',
        },
      },
    },
  })();

  const navigate = useNavigate();
  const location = useLocation();
  const careNavigatorStore = useStores().careNavigatorStore;
  const memberSummaryStore = useStores().memberSummaryStore;
  const loginPageStore = useStores().loginPageStore;
  const state: any = location.state;
  const MEMBER_ID = state.memberId;
  const CASE_ID = state.caseId;
  const PATIENT_ID = state.patientId;
  const isCaseClosedHistory = state.isCaseClosed ?? false;

  const pageConstants = componentConstants.PAGES.PATIENT_DETAILS_PAGE;
  const { PATIENT_ENGAGEMENT_REPORT } = PatientCasesMessages;

  const { snackbarState, openSnackbar, handleCloseSnackBar } = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const [isEndCaseModalOpen, setIsEndCaseModalOpen] = useState<boolean>(false);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [
    isUpdateSurveyModalOpen,
    setIsUpdateSurveyModalOpen,
  ] = useState<boolean>(false);
  const [
    patientProfileAccordionData,
    setPatientProfileAccordionData,
  ] = useState<PatientProfileAccordionData>({
    values: ['-', '-', '-'],
    hospitalVisitData: hospitalVisitData,
    totalCostData: [['-', '-']],
    recentVisitData: [['-', '-']],
    memberRelationShipData: '',
    enrollmentData: enrollmentData,
  });
  const [
    healthProfileAccordionData,
    setHealthProfileAccordionData,
  ] = useState<HealthProfileAccordionData>({
    currentCondition: '-',
    latentRiskData: [['-', '-']],
  });
  const [engagementReport, setEngagementReport] = useState<any>({});
  const [triggerAPI, setTriggerAPI] = useState<boolean>(false);
  const [endCaseData, setEndCaseData] = useState<any>({
    case_survey_id: '',
    case_id: '',
    cn_id: '',
    cn_name: '',
    cn_role: '',
    patient_id: '',
    group_id: '',
    case_type: '',
    referral: '',
    saving: null,
    roi: null,
    responsivenes: '',
    responsivenes_desc: '',
    rating: null,
    review: '',
  });
  const [referrals, setReferrals] = useState<string[]>([]);
  const [caseTypes, setCaseTypes] = useState<string[]>([]);
  const [isCaseClosed, setIsCaseClosed] = useState<boolean>(
    isCaseClosedHistory,
  );
  const [interactionData, setInteractionData] = useState<any>({
    interaction_id: '',
    interaction_date: '',
    type: '',
    mode: '',
    referral: '',
    details: '',
  });
  const [isUpdateInteraction, setIsUpdateInteraction] = useState<boolean>(
    false,
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [patient, setPatient] = useState<any>({
    contact: '',
    address: '',
  });

  const updateInteractionDataFields = [
    'interaction_id',
    'interaction_date',
    'type',
    'mode',
  ];

  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
  };

  const handleCareNavigatorClick = (_id: string) => {
    memberSummaryStore.removePathDetails();
    const pathDetails: any[] = [];
    pathDetails.push([
      'text',
      'Care Navigator',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => navigate(PATIENT_CASES),
    ]);

    memberSummaryStore.setPathDetails(pathDetails);

    navigate(CARE_NAVIGATOR_PROFILE, { state: { care_navigator_id: _id } });
  };

  const handleOnClickPlan = async () => {
    navigate(PLAN_DOCUMENTS_INTERNAL, { state: { tabIndex: 1 } });
  };

  const CareNavigatorStatsTable = () => {
    const CareNavigatorStatsTableColumns: Columns[] = [
      {
        name: '',
        align: 'left',
        width: '',
        cellStyles: {
          width: '60%',
        },
      },
      { name: '', align: 'left', width: '', cellStyles: {} },
    ];

    const tableBodyStyles = {
      '& tr:first-child': {
        backgroundColor: patientAccessTheme.palette.grey[700],
        color: patientAccessTheme.palette.status.white,
      },
      '& tr:first-child td': {
        color: 'inherit',
      },
    };
    const rootStyles = {
      '& .MuiGrid-item': {
        width: '100%',
      },
    };

    return (
      <BasicTable
        columns={CareNavigatorStatsTableColumns}
        rowsData={engagementReport?.stats}
        hideTableHeader={true}
        tableBodyStyles={tableBodyStyles}
        width="100%"
        rootStyles={rootStyles}
        displayType="row"
      />
    );
  };
  const EngagementTable = () => {
    const cellStyles = {
      fontSize: '14px',
      lineHeight: '23px',
      color: patientAccessTheme.palette.text.mediumemphasis,
      fontWeight: 400,
    };
    const EngagementTableColumns: Columns[] = [
      {
        name: 'DATE',
        align: 'left',
        width: '100px',
        cellStyles: cellStyles,
      },
      { name: 'MEDIUM', align: 'left', width: '100px', cellStyles: cellStyles },
      {
        name: 'CONDITION',
        align: 'left',
        width: '100px',
        cellStyles: cellStyles,
      },
      {
        name: 'ENGAGEMENT SESSION',
        align: 'left',
        width: '200px',
        cellStyles: cellStyles,
      },
      loginPageStore.isCareNavigator && {
        name: '',
        align: 'center',
        width: '30px',
        cellStyles: cellStyles,
      },
    ].filter(Boolean) as Columns[];

    const rootStyles = {
      '& .MuiGrid-item': {
        width: '100%',
      },
    };

    return (
      <CardComponent
        title={PATIENT_ENGAGEMENT_REPORT.ENGAGEMENT.TITLE}
        subTitle={PATIENT_ENGAGEMENT_REPORT.ENGAGEMENT.SUBTITLE}
        body={
          <BasicTable
            columns={EngagementTableColumns}
            rowsData={engagementReport.interaction}
            displayType="box"
            width="100%"
            rootStyles={rootStyles}
          />
        }
      />
    );
  };
  const RatingReviewCard = () => {
    const { classes } = makeStyles()({
      ratingReviewContainer: {
        border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        padding: '1rem',
      },
      ratingContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        flex: 1,
        borderRight: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      },
      reviewContainer: {
        flex: 1,
        marginLeft: '1rem',
      },
      details: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
      },
    })();

    const styleProps = {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
    };

    return (
      <CardComponent
        title={PATIENT_ENGAGEMENT_REPORT.RATING.TITLE}
        subTitle={PATIENT_ENGAGEMENT_REPORT.RATING.SUBTITLE}
        body={
          <Grid container className={classes.ratingReviewContainer}>
            <Grid className={classes.ratingContainer}>
              {engagementReport.rating?.rating ? (
                <StarRating
                  value={engagementReport.rating?.rating}
                  showRating={
                    <Grid className={classes.details}>
                      <Typography
                        fontSize="14px"
                        lineHeight="22px"
                        fontType="regular"
                        textColor={patientAccessTheme.palette.text.lowemphasis}
                      >
                        ({engagementReport.rating?.rating?.toFixed(1)})
                      </Typography>
                      <Typography
                        fontSize="14px"
                        lineHeight="22px"
                        fontType="regular"
                        textColor={getRatingColor(
                          Math.ceil(engagementReport.rating?.rating),
                        )}
                      >
                        {getRatingText(
                          Math.ceil(engagementReport.rating?.rating),
                        )}
                      </Typography>
                    </Grid>
                  }
                  size={'medium'}
                  precision={0.5}
                  color={getRatingColor(
                    Math.ceil(engagementReport.rating?.rating),
                  )}
                  readOnly={true}
                />
              ) : (
                <Typography
                  fontSize="14px"
                  lineHeight="19px"
                  fontType="medium"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  No Rating Given
                </Typography>
              )}
              <Grid
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Grid>
                  <Typography
                    fontSize="14px"
                    lineHeight="19px"
                    fontType="medium"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                  >
                    {engagementReport.rating?.name}
                  </Typography>
                </Grid>
                <Grid>
                  <Typography
                    fontSize="14px"
                    lineHeight="19px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.lowemphasis}
                  >
                    reviewed
                  </Typography>
                </Grid>

                <Grid>
                  <Typography
                    textColor={patientAccessTheme.palette.primary.main}
                    fontSize="14px"
                    lineHeight="19px"
                    fontType="medium"
                    styleProps={styleProps}
                    onClick={() => {
                      handleCareNavigatorClick(
                        engagementReport.rating?.careNavigatorId,
                      );
                    }}
                  >
                    {engagementReport.rating?.careNavigatorName}
                    {engagementReport.rating?.careNavigatorRole ===
                    'care_navigator_nurse' ? (
                      <Nurse />
                    ) : (
                      <Pharmacist />
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid className={classes.reviewContainer}>
              <Typography
                textColor={patientAccessTheme.palette.text.highemphasis}
                fontSize="12px"
                lineHeight="19px"
              >
                {engagementReport.rating?.review}
              </Typography>
            </Grid>
          </Grid>
        }
      />
    );
  };

  //Api calls
  const getPatientProfileData = async () => {
    const url =
      properties.aiqBaseUrl +
      properties.membersBaseUrl +
      properties.patientProfile;

    const requestBody = {
      eg_nid: [careNavigatorStore.eg_nid],
      member_unique_id: MEMBER_ID,
      memberIdentification: loginPageStore.memberIdentification,
    };
    return await axios.post(url, requestBody);
  };

  const getHealthProfileData = async () => {
    const url =
      properties.aiqBaseUrl +
      properties.membersBaseUrl +
      properties.healthDetails;

    const requestBody = {
      eg_nid: [careNavigatorStore.eg_nid],
      member_unique_id: MEMBER_ID,
    };
    return await axios.post(url, requestBody);
  };
  const getEngagmentReport = async () => {
    const url =
      properties.aiqBaseUrl +
      properties.caseSurveyBaseUrl +
      properties.getEngagementReport;

    const requestBody = {
      case_id: CASE_ID,
    };

    return await axios.post(url, requestBody);
  };

  const getCaseDetails = async () => {
    const url =
      properties.aiqBaseUrl + properties.casesBaseUrl + properties.caseById;
    const requestBody = {
      case_id: CASE_ID,
    };
    return await axios.post(url, requestBody);
  };

  const getReferrals = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.dynamicFieldsBaseUrl +
        properties.getDynamicFields,
      {
        field: 'referrals',
      },
    );
  };

  const getCaseTypes = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.dynamicFieldsBaseUrl +
        properties.getDynamicFields,
      {
        field: 'case_types',
      },
    );
  };

  const endCase = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.caseSurveyBaseUrl +
          properties.createCaseSurvey,
        endCaseData,
      )
      .then((response) => {
        if (response.status === 200) {
          openSnackbar('Case ended succesfully', 'success', 6000);
        }
        setIsEndCaseModalOpen(false);
        setIsCaseClosed(true);
      })
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleUpdateSurvey = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.caseSurveyBaseUrl +
          properties.updateCaseSurvey,
        {
          case_survey_id: endCaseData?.case_survey_id,
          case_type: endCaseData?.case_type,
          referral: endCaseData?.referral,
          saving: endCaseData?.saving,
          roi: endCaseData?.roi,
          responsivenes: endCaseData?.responsivenes,
          responsivenes_desc: endCaseData?.responsivenes_desc,
          rating: endCaseData?.rating,
          review: endCaseData?.review,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          openSnackbar('Survey updated succesfully', 'success', 6000);
        }
        setIsUpdateSurveyModalOpen(false);
        apiCalls(true);
      })
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleContactChange = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.patientsBaseUrl +
          properties.updateContact,
        {
          patient_id: PATIENT_ID,
          member_id: MEMBER_ID,
          contact: patient.contact,
          address: patient.address,
        },
      )
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const handleEditInteraction = (data: any) => {
    setInteractionData((prev: any) => ({
      ...prev,
      interaction_id: data._id,
      interaction_date: new Date(data.interaction_date)
        .toISOString()
        .slice(0, 16),
      type: data.type,
      mode: data.mode,
      referral: data.referral,
      details: data.details?.[0],
    }));
    setIsUpdateInteraction(true);
  };

  const handleupdateInteraction = async () => {
    const formattedDate = moment
      .parseZone(interactionData.interaction_date)
      .format('YYYY-MM-DDTHH:mm:ss[Z]');

    await axios
      .post(
        properties.aiqBaseUrl +
          properties.interactionBaseURL +
          properties.updateInteraction,
        {
          interaction_id: interactionData.interaction_id,
          interaction_date: formattedDate,
          type: interactionData.type,
          mode: interactionData.mode,
          referral: interactionData.referral,
          details: interactionData.details,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          openSnackbar('Interaction updated succesfully', 'success', 6000);
        }
        setIsUpdateInteraction(false);
        apiCalls(true);
      })
      .catch((error) =>
        openSnackbar(error.response?.data?.error, 'error', 6000),
      );
  };

  const apiCalls = async (isCaseClosed: boolean) => {
    setLoading(true);

    const promises = [
      getPatientProfileData(),
      getHealthProfileData(),
      getReferrals(),
      getCaseTypes(),
    ];
    if (isCaseClosed) {
      promises.push(getEngagmentReport());
    } else {
      promises.push(getCaseDetails());
    }

    await Promise.allSettled(promises).then((responses) => {
      setLoading(false);
      //getPatientProfileData()
      if (responses[0]?.status === 'fulfilled') {
        setPatientProfileAccordionData(
          getPatientProfileAccordionData(
            responses[0]?.value.data?.data,
            MEMBER_ID,
            handleOnClickPlan,
          ),
        );
      } else if (responses[0]?.status === 'rejected') {
        openSnackbar(responses[0]?.reason, 'error', 5000);
      }

      //getHealthProfileData();
      if (responses[1]?.status === 'fulfilled') {
        setHealthProfileAccordionData(
          getHealthProfileAccordionData(responses[1]?.value.data?.data),
        );
      } else if (responses[1]?.status === 'rejected') {
        openSnackbar(responses[1]?.reason, 'error', 5000);
      }

      //getReferrals()
      if (responses[2].status === 'fulfilled') {
        setReferrals(responses[2].value?.data?.data);
      }

      //getCaseTypes()
      if (responses[3].status === 'fulfilled') {
        setCaseTypes(responses[3].value?.data?.data);
      }

      //getEngagementReport();
      if (isCaseClosed && responses[4]?.status === 'fulfilled') {
        setEngagementReport(
          formatEngagementReportData(
            responses[4]?.value.data?.data,
            handleEditInteraction,
            loginPageStore.isCareNavigator,
          ),
        );
        const caseData = responses[4].value.data?.data?.stats ?? {};
        setPatient({
          contact: caseData?.contact || '',
          address: caseData?.address || '',
        });
        setEndCaseData((prev: any) => ({
          ...prev,
          case_survey_id: caseData?.caseSurveyId,
          case_type: caseData?.caseType,
          referral: caseData?.referral,
          saving: caseData?.savings,
          roi: caseData?.roi,
          responsivenes: caseData?.responsiveness,
          responsivenes_desc: caseData?.responsiveness_desc,
          rating: caseData?.rating,
          review: caseData?.review,
        }));
      } else if (isCaseClosed && responses[4]?.status === 'rejected') {
        openSnackbar(responses[4]?.reason, 'error', 5000);
      }

      //getCaseDetails()
      if (!isCaseClosed && responses[4].status === 'fulfilled') {
        const caseData = responses[4].value.data?.data[0];
        if (caseData?.case_status === 'close') {
          setIsCaseClosed(true);
        }
        setPatient({
          contact: caseData?.patient_id?.contact || '',
          address: caseData?.patient_id?.address || '',
        });
        setEndCaseData((prev: any) => ({
          ...prev,
          case_id: caseData?._id,
          cn_id: caseData?.cn_id,
          cn_name: caseData?.cn_name,
          cn_role: caseData?.cn_role,
          group_id: caseData?.group_id,
          patient_id: caseData?.patient_id?._id,
        }));
      }
      if (responses[4].status === 'rejected') {
        openSnackbar(responses[4]?.reason, 'error', 5000);
      }

      setTriggerAPI(true);
    });

    setLoading(false);
  };

  useEffect(() => {
    apiCalls(isCaseClosed);
  }, [isCaseClosed]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Grid container style={{ width: '100%' }}>
        <Grid className={classes.sideContentGridItem}>
          <PatientDetailsSideContent
            showContact={true}
            patientState={[patient, setPatient]}
            handleContactChange={handleContactChange}
            memberId={MEMBER_ID}
            patientProfileAccordionData={patientProfileAccordionData}
            healthProfileAccordionData={healthProfileAccordionData}
          />
        </Grid>
        <Grid
          container
          className={classes.mainContentGridItem}
          sx={{
            flexDirection: 'column',
          }}
        >
          {isCaseClosed ? (
            <Grid className={classes.closedCaseContainer}>
              <PageTitleAndSubTitle
                title={PATIENT_ENGAGEMENT_REPORT.TITLE}
                subTitle={PATIENT_ENGAGEMENT_REPORT.SUBTITLE}
                customButton={loginPageStore.isCareNavigator}
                customButtonText={PATIENT_ENGAGEMENT_REPORT.EDIT_SURVEY}
                customButtonClick={() => setIsUpdateSurveyModalOpen(true)}
                customButtonStartIcon={<Edit className={classes.editIcon} />}
                customButtonVariant={'outlined'}
                customButtonWidth={'fit-content'}
              />
              <Grid className={classes.tabsContainer}>
                <TabSelection
                  selectedTabIndex={selectedTabIndex}
                  setSelectedTabIndex={handleSelectedIndex}
                  tabList={PATIENT_ENGAGEMENT_REPORT.TABS}
                />

                <Divider
                  style={{
                    backgroundColor: patientAccessTheme.palette.grey[300],
                    marginLeft: '-34px',
                  }}
                />

                {selectedTabIndex === 0 && (
                  <>
                    <CareNavigatorStatsTable />
                    <EngagementTable />
                    <RatingReviewCard />
                  </>
                )}
                {selectedTabIndex === 1 && (
                  <PatientCaseHistoryTab
                    memberId={PATIENT_ID}
                    triggerAPI={triggerAPI}
                  />
                )}
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid
                container
                style={{
                  margin: '31px 34px',
                  width: '95%',
                }}
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <TextField
                  placeholder={pageConstants.SEARCH_HISTORY}
                  height={'40px'}
                  width={'401px'}
                  startAdornment={
                    <SimpleSearch
                      height="20px"
                      width="20px"
                      stroke={patientAccessTheme.palette.text.lowemphasis}
                    />
                  }
                  showStartAdornment={true}
                />
                {loginPageStore.isCareNavigator && (
                  <Button
                    color="primary"
                    variant="contained"
                    height="36px"
                    width="120px"
                    label={'End Case'}
                    disabled={isCaseClosed}
                    onClick={() => {
                      interactions?.length > 0 && setIsEndCaseModalOpen(true);
                    }}
                  />
                )}
              </Grid>
              <Grid>
                <PatientCasesTabs
                  memberId={MEMBER_ID}
                  caseId={CASE_ID}
                  triggerAPI={triggerAPI}
                  patientId={PATIENT_ID}
                  interactionState={[interactions, setInteractions]}
                />
              </Grid>
            </>
          )}
        </Grid>
        {isEndCaseModalOpen && (
          <EndCaseCareNavModel
            open={isEndCaseModalOpen}
            handleClose={() => {
              setIsEndCaseModalOpen(false);
            }}
            handleSave={endCase}
            dataState={[endCaseData, setEndCaseData]}
            referrals={referrals}
            caseTypes={caseTypes}
          />
        )}
        {isUpdateSurveyModalOpen && (
          <EndCaseCareNavModel
            open={isUpdateSurveyModalOpen}
            handleClose={() => {
              setIsUpdateSurveyModalOpen(false);
            }}
            handleSave={handleUpdateSurvey}
            dataState={[endCaseData, setEndCaseData]}
            referrals={referrals}
            caseTypes={caseTypes}
            update={true}
          />
        )}
        {isUpdateInteraction && (
          <AddInteractionModal
            open={isUpdateInteraction}
            handleClose={() => setIsUpdateInteraction(false)}
            handleSave={handleupdateInteraction}
            isSaveButtonDisabled={
              !updateInteractionDataFields.every(
                (field) => interactionData[field],
              )
            }
            dataState={[interactionData, setInteractionData]}
            referrals={referrals}
            update={true}
          />
        )}
      </Grid>
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default PatientCasesPage;
